// Layer 3 (Transformation) — the Maintenance status band (polish.P21, locked
// comp `.impeccable/mocks/decision/maintenance.html` variant A, adjudication 4).
// Pure: no React, no engine mutation, no clock read of its own.
//
// One band replaces the two KPI strips the Asset register and PM schedule tabs
// each printed (both carried Overdue PM and PM compliance). Four cells, each a
// figure over what it is measured against:
//
//   1. Condition alerts — `strategyKpis().alerts`: the points at `warn`, of all
//      points; the comparison names the alerting point's latest reading
//      against its own Reading's band high, then the rising points.
//   2. Overdue PM — `strategyKpis().overdue`, the register's own read; the
//      comparison names the worst row's item, task and days past its due date.
//   3. Due today — `pmKpis().dueToday` against the week `pmKpis().thisWeek`
//      holds (tasks, hours, technicians, dates). After the D16 seed fix the
//      week no longer carries the BM01 bearing R&R, so this reads what is left.
//   4. PM compliance — `strategyKpis().compliance` (which is
//      `pmKpis().compliance` unchanged) over the 30-day basis, with a meter
//      whose ticks decode `complianceTone` (adjudication 7).
//
// durable: the Maintenance status band over the PM schedule and the monitoring points (ADR-0012).

import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import type { SessionState } from '@/versions/v1/schema/scenario';
import type { VizTone } from '@/versions/v1/modules/_viz/viz-tone';
import { addMinutes, formatDayMonth } from '@/versions/v1/shared/clock.derive';
import { formatHours } from './Downtime.transform';
import { BASIS_DAYS } from './downtime.seed';
import type { PmTask } from './pm.seed';
import {
  COMPLIANCE_OK_PCT,
  COMPLIANCE_WARN_PCT,
  pmKpis,
  pmTaskRows,
} from './PmSchedule.transform';
import type { ConditionMonitoringPoint } from './Scene.transform';
import { conditionDetail } from './ConditionDetail.transform';
import { assetRegisterRows, itemsByCriticality, strategyKpis } from './Strategy.transform';

/** A run of comparison text; `strong` marks the lead term (600 ink-secondary). */
export type BandSegment = { text: string; strong?: boolean };

export type BandCell = {
  id: 'alerts' | 'overdue' | 'due-today' | 'compliance';
  label: string;
  figure: string;
  /** Qualifier on the figure's baseline (`of 7 points`, `tasks · 8.0 h`). */
  unit?: string;
  tone: VizTone;
  comparison: readonly BandSegment[];
  meter?: { pct: number; tone: VizTone; ticks: readonly number[] };
};

/** `08–14 Jun` inside one month, `29 Jun–05 Jul` across two. */
export function compactDayRange(startAt: SeriesTime, endAt: SeriesTime): string {
  const start = formatDayMonth(startAt);
  const end = formatDayMonth(endAt);
  return start.slice(3) === end.slice(3) ? `${start.slice(0, 2)}–${end}` : `${start}–${end}`;
}

function joinNames(names: readonly string[]): string {
  return names.join(', ');
}

export function maintenanceBand(input: {
  points: readonly ConditionMonitoringPoint[];
  tasks: readonly PmTask[];
  state: SessionState;
}): BandCell[] {
  const { points, tasks, state } = input;
  const cursor = state.cursor;
  const kpis = pmKpis(tasks, cursor);
  const register = strategyKpis(assetRegisterRows(itemsByCriticality(), tasks, points, cursor), tasks, points, cursor);

  // 1 · Condition alerts.
  const alerting = points.filter((point) => point.statusChip === 'warn');
  const rising = points.filter((point) => point.statusChip === 'rising');
  const alertSegments: BandSegment[] = [];
  const firstAlert = alerting[0];
  if (firstAlert) {
    const read = conditionDetail(firstAlert.id, points, state);
    const warn = read?.chart?.references.find((ref) => ref.tone === 'warn');
    alertSegments.push({ text: firstAlert.item, strong: true });
    if (read?.latest && warn && read.reading) {
      alertSegments.push({ text: ` ${read.latest.valueLabel} vs ${warn.value} ${read.reading.unit} warning` });
    }
    if (alerting.length > 1) alertSegments.push({ text: ` · +${alerting.length - 1} more` });
  }
  if (rising.length > 0) {
    if (alertSegments.length > 0) alertSegments.push({ text: ' · ' });
    alertSegments.push({ text: `${rising.length} rising`, strong: true });
    alertSegments.push({ text: ` (${joinNames(rising.map((point) => point.item))})` });
  }
  if (alertSegments.length === 0) {
    alertSegments.push({ text: `${points.length} stable`, strong: true });
  }

  // 2 · Overdue PM — the worst row is the earliest-due open row past its date.
  const overdueRows = pmTaskRows(tasks, cursor).filter((row) => row.overdue);
  const worst = register.overdue.worst;
  const worstRow = worst ? overdueRows.find((row) => row.asset === worst.label) ?? overdueRows[0] : undefined;
  const overdueSegments: BandSegment[] = worst && worstRow
    ? [
        { text: worst.label, strong: true },
        { text: ` · ${worstRow.task} · ` },
        { text: `${worst.daysOverdue} days`, strong: true },
        { text: ` past ${worstRow.due}` },
      ]
    : [{ text: register.overdue.sub }];

  // 3 · Due today against the week.
  const cursorDay = `${cursor.slice(0, 10)}T00:00` as SeriesTime;
  const weekRange = compactDayRange(cursorDay, addMinutes(cursorDay, 6 * 24 * 60));
  const dueSegments: BandSegment[] = [
    { text: 'of ' },
    { text: `${kpis.thisWeek.count} · ${formatHours(kpis.thisWeek.hours)}`, strong: true },
    { text: ` this week · ${kpis.thisWeek.technicianCount} technicians · ${weekRange}` },
  ];

  // 4 · Compliance.
  const compliance = register.compliance;
  const complianceSegments: BandSegment[] = [
    { text: `${compliance.onTime} of ${compliance.due}`, strong: true },
    { text: ' on time' },
  ];

  return [
    {
      id: 'alerts',
      label: 'Condition alerts',
      figure: register.alerts.value,
      unit: register.alerts.sub,
      tone: register.alerts.count > 0 ? 'warn' : 'neutral',
      comparison: alertSegments,
    },
    {
      id: 'overdue',
      label: 'Overdue PM',
      figure: register.overdue.value,
      tone: register.overdue.count > 0 ? 'bad' : 'neutral',
      comparison: overdueSegments,
    },
    {
      id: 'due-today',
      label: 'Due today',
      figure: kpis.dueToday.value,
      unit: `tasks · ${kpis.dueToday.sub}`,
      tone: 'neutral',
      comparison: dueSegments,
    },
    {
      id: 'compliance',
      label: `PM compliance · ${BASIS_DAYS} days`,
      figure: compliance.value,
      tone: compliance.tone,
      comparison: complianceSegments,
      meter: { pct: compliance.pct, tone: compliance.tone, ticks: [COMPLIANCE_WARN_PCT, COMPLIANCE_OK_PCT] },
    },
  ];
}
